import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {getSupplierOrderDetail} from '../../services/supplierOrderService';
import ShipmentTracker from './ShipmentTracker';
import ImageGallery from './ImageGallery';
import OrderStatusBadge from './atoms/OrderStatusBadge';
import { IoChevronDownOutline } from "react-icons/io5";
import {miladiToShamsi} from "../../utils/miladiToShamsi";
import { Link } from "react-router-dom";
import {getOrderReviewable} from "../../services/orderService";
import SupplierRenderReviewButton from "./Rating/supplier/SupplierRenderReviewButton";


const SupplierOrderAccordion = ({ order }) => {
  const [isOpen, setIsOpen] = useState(false);

  const UNIT_TRANSLATIONS = {
  'count': 'عدد',
  'package': 'بسته',
  'weight': 'کیلو گرم',
  'length': 'متر',
  'area': 'متر مربع',
  'litr': 'لیتر',
  'volume': 'متر مکعب'
};

  const { data: orderDetails, isLoading } = useQuery({
    queryKey: ['orderDetail', order.id],
    queryFn: () => getSupplierOrderDetail(order.id),
    enabled: isOpen,
  });

      const { data: reviewables } = useQuery({
        queryKey: ['orderReviewables', order.id],
        queryFn: () => getOrderReviewable(order.id),
        enabled: isOpen,
      });

      const shipmentReviewMap = {};
      const itemReviewMap = {};

      if (reviewables?.shipments) {
        reviewables.shipments.forEach((shipment) => {
          shipmentReviewMap[shipment.id] = shipment.review;

          shipment.items.forEach((item) => {
            itemReviewMap[item.id] = item.review;
          });
        });
      }

  return (
      <div className="border border-gray-200 rounded-xl overflow-hidden bg-white mb-4">
          {/* Order Header Card */}
          <div className="p-5 bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-shadow">

              {/* سطر اول: کد سفارش و وضعیت */}
              <div className="flex justify-between items-center pb-4 border-b border-gray-50">
                  <div className="flex items-center gap-2">
                      <span className="text-gray-400 text-xs font-bold uppercase tracking-wider">سفارش</span>
                      <span className="text-gray-900 font-black text-sm uppercase">ORD-{order.id}</span>
                      {order.has_customer_review && (
                          <span className="text-blue-700 font-black bg-blue-100 p-[6px] border-blue-900 rounded-[8px] text-sm uppercase">دارای نظر ثبت شده</span>
                      )}
                  </div>
                  <OrderStatusBadge status={order.fulfillment_status}/>
              </div>

              {/* سطر دوم: اطلاعات تکمیلی (تاریخ، مبلغ، تعداد) */}
              <div className="grid grid-cols-3 gap-4 py-5">
                  <div className="flex flex-col gap-1">
                      <span className="text-gray-400 text-[11px] font-bold">نام مشتری</span>
                      <span className="text-gray-700 text-sm font-medium tabular-nums">
                {order.customer_name}
              </span>
                  </div>
                  <div className="flex flex-col gap-1">
                      <span className="text-gray-400 text-[11px] font-bold">شماره موبایل مشتری</span>
                      <span className="text-gray-700 text-sm font-medium tabular-nums">
                {order.customer_phone}
              </span>
                  </div>
                  <div className="flex flex-col gap-1">
                      <span className="text-gray-400 text-[11px] font-bold">تاریخ ثبت</span>
                      <span className="text-gray-700 text-sm font-medium tabular-nums">
                {miladiToShamsi(order.created_at)}
              </span>
                  </div>

                  <div className="flex flex-col gap-1 border-r border-gray-100 pr-4">
                      <span className="text-gray-400 text-[11px] font-bold">مبلغ کل</span>
                      <div className="flex items-center gap-1">
                <span className="text-gray-900 text-sm font-black tabular-nums">
                  {Number(order.total_amount).toLocaleString()}
                </span>
                          <span className="text-gray-400 text-[10px]">تومان</span>
                      </div>
                  </div>
                  <div className="flex flex-col gap-1 border-r border-gray-100 pr-4">
                      <span className="text-gray-400 text-[11px] font-bold">تعداد محموله</span>
                      <span className="text-gray-700 text-sm font-medium">
                        {order.shipments_count} محموله
                      </span>
                  </div>
                  <div className="flex flex-col gap-1 border-r border-gray-100 pr-4">
                      <span className="text-gray-400 text-[11px] font-bold">تعداد اقلام</span>
                      <span className="text-gray-700 text-sm font-medium">
                        {order.items_count} قلم کالا
                      </span>
                  </div>
              </div>

              {/* سطر سوم: تصاویر کالاها */}
              <div className="pt-2">
                  <p className="text-[11px] font-bold text-gray-400 mb-2">کالاهای این سفارش:</p>
                  <ImageGallery items={order.items}/>
              </div>

          </div>

          {/* Accordion Trigger */}
          <button
              onClick={() => setIsOpen(!isOpen)}
              className="w-full py-2 bg-gray-50 flex items-center justify-center gap-2 text-gray-500 text-sm hover:bg-gray-100 transition-colors border-t border-gray-100"
          >
              <span>مشاهده جزئیات</span>
              <IoChevronDownOutline className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}/>
          </button>

          {/* Accordion Content */}
          {isOpen && (
              <div className="p-6 bg-white border-t border-gray-100">
                  {isLoading ? (
                      <div className="flex justify-center p-10">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      </div>
                  ) : (
                      orderDetails?.shipments?.map((shipment) => {
                          const shipmentReview = shipmentReviewMap[shipment.id];

                          return (
                              <div key={shipment.id} className="mb-10 last:mb-0">
                                  <div className={'flex justify-between items-center mb-4'}>
                                      <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                                          <span className="w-2 h-6 bg-blue-600 rounded-full"></span>
                                          محموله SHI-{shipment.id}
                                      </h3>

                                      <SupplierRenderReviewButton
                                          review={shipmentReview}
                                          entityType="shipment"
                                          shipmentId={shipment.id}
                                          orderId={orderDetails?.id}
                                          info={shipment}
                                      />
                                  </div>

                                  <ShipmentTracker shipment={shipment}  isCustomer = {false}/>

                                  {/* Items Table */}
                                  <div className="overflow-x-auto rounded-lg border border-gray-100 mt-4">
                                      <table className="w-full text-right text-sm">
                                          <thead className="bg-gray-50 text-gray-500 font-bold">
                                          <tr>
                                              <th className="p-4">کالا</th>
                                              <th className="p-4 text-center">مقدار</th>
                                              <th className="p-4 text-center">قیمت واحد</th>
                                              <th className="p-4 text-center">تخفیف</th>
                                              <th className="p-4 text-center text-blue-600 font-black">قیمت نهایی</th>
                                              <th className="p-4 text-center text-blue-600 font-black">نظر خریدار</th>
                                          </tr>
                                          </thead>
                                          <tbody className="divide-y divide-gray-50">
                                          {shipment.items.map((item) => {
                                              const itemReview = itemReviewMap[item.id];

                                              return (

                                                  <tr key={item.id} className="hover:bg-blue-50/30 transition-colors">
                                                      <td className="p-4">
                                                          <div className="flex items-center gap-4">
                                                              <Link
                                                                  to={`/shopping/product-detail/${item.product_slug}`}
                                                                  className="w-16 h-16 bg-gray-100 rounded-xl overflow-hidden flex-shrink-0"
                                                              >
                                                                  <img src={item.product_image} alt=""
                                                                       className="w-16 h-16 rounded-lg object-cover bg-gray-100"/>
                                                              </Link>
                                                              <div>
                                                                  <p className="font-bold text-gray-800">{item.product_name}</p>
                                                                  <p className="text-xs text-gray-400 mt-1">{item.product_category} | {item.product_brand}</p>
                                                              </div>
                                                          </div>
                                                      </td>
                                                      <td className="p-4 text-center font-bold text-gray-600 leading-relaxed">
                                                          {item.qty_ordered} <br/>
                                                          <span
                                                              className="text-[10px] font-normal">{UNIT_TRANSLATIONS[item.unit_of_measure]}</span>
                                                      </td>
                                                      <td className="p-4 text-center tabular-nums">{Number(item.unit_price).toLocaleString()} تومان</td>
                                                      <td className="p-4 text-center tabular-nums text-red-500">
                                                          {item.discount_percent}% <br/>
                                                          <span
                                                              className="text-[10px]">({Number(item.discount_amount).toLocaleString()} تومان)</span>
                                                      </td>
                                                      <td className="p-4 text-center font-black text-gray-800 tabular-nums">
                                                          {Number(item.final_total).toLocaleString()} تومان
                                                      </td>
                                                      <td className="p-4 text-center font-black text-gray-800 tabular-nums">
                                                          <SupplierRenderReviewButton
                                                              review={itemReview}
                                                              entityType="shipment_item"
                                                              shipmentId={shipment.id}
                                                              shipmentItemId={item.id}
                                                              orderId={orderDetails?.id}
                                                              info={item}
                                                          />

                                                      </td>
                                                  </tr>
                                              )
                                          })}
                                          </tbody>
                                      </table>
                                  </div>
                              </div>
                          );
                      })
                  )}
              </div>
          )}
      </div>
  );
};

export default SupplierOrderAccordion;
